import axiosInstance from "../../utils/axiosConfig";
import { compactParams } from "../../lib/api/params";
import type { IsoDateRange } from "../../lib/adminExport/datePresets";

export const DIRECTORY_EXPORT_PAGE_LIMIT = 100;
export const MAX_DIRECTORY_EXPORT_PAGES = 200;

async function fetchDirectoryPages<T>(
  endpoint: string,
  extract: (root: {
    clients?: T[];
    businesses?: T[];
    users?: T[];
    meta?: { totalPages?: number };
  }) => T[],
  range?: IsoDateRange | null,
  search?: string
): Promise<T[]> {
  const items: T[] = [];
  let totalPages = 1;

  for (let page = 1; page <= totalPages; page++) {
    if (page > MAX_DIRECTORY_EXPORT_PAGES) {
      throw new Error(
        `Export is limited to ${
          MAX_DIRECTORY_EXPORT_PAGES * DIRECTORY_EXPORT_PAGE_LIMIT
        } rows. Narrow your search or date range.`
      );
    }

    const { data } = await axiosInstance.get<{ data?: unknown }>(endpoint, {
      params: compactParams({
        page,
        limit: DIRECTORY_EXPORT_PAGE_LIMIT,
        search,
        startDate: range?.startDate,
        endDate: range?.endDate,
      }),
    });

    const root = (data?.data ?? {}) as {
      clients?: T[];
      businesses?: T[];
      users?: T[];
      meta?: { totalPages?: number };
    };
    const batch = extract(root);

    if (page === 1) {
      totalPages = Math.max(1, root.meta?.totalPages ?? 1);
    }

    items.push(...batch);
    if (batch.length === 0) break;
  }

  return items;
}

export function fetchClientsForExport(range?: IsoDateRange | null) {
  return fetchDirectoryPages<Record<string, unknown>>(
    "api/users/clients/",
    (root) => (Array.isArray(root.clients) ? root.clients : []),
    range
  );
}

export function fetchMerchantsForExport(range?: IsoDateRange | null) {
  return fetchDirectoryPages<Record<string, unknown>>(
    "api/users/merchants/",
    (root) => (Array.isArray(root.businesses) ? root.businesses : []),
    range
  );
}

export function fetchCustomersForExport(range?: IsoDateRange | null) {
  return fetchDirectoryPages<Record<string, unknown>>(
    "api/users/",
    (root) => (Array.isArray(root.users) ? root.users : []),
    range
  );
}
